"use strict";
// Marketing — tracking tags, cookie consent, ad attribution, server-side
// conversions, Google Search Console, WhatsApp chat.
//
//   published site (production only)              studio
//   ───────────────────────────────                ──────
//   core.deploy.transform ─▶ <head>: consent defaults (Consent Mode v2)
//                                   + dunea-marketing runtime
//   runtime: consent banner ─▶ loads marketing.tag providers that are allowed
//            captures utm_* / gclid / fbclid / ttclid ─▶ sent with every checkout
//   payments.payment.succeeded ─▶ server-side conversions (Meta CAPI, GA4 MP)
//                                 only when the buyer consented to it
//   attribution report: paid revenue grouped by source / medium / campaign
//
// Tags never load in preview, so building and testing a site doesn't pollute
// the owner's analytics or ad accounts.
Object.defineProperty(exports, "__esModule", { value: true });
exports.WHATSAPP_CHAT_PLUGIN = exports.SEARCH_CONSOLE_PLUGIN = exports.MARKETING_TAG_PLUGINS = exports.MARKETING_TAGS = exports.MARKETING_PLUGIN = exports.MARKETING_TAG_FEATURES = void 0;
exports.MARKETING_TAG_FEATURES = [
    'pageViews',
    /** Browser conversion events (purchase, lead). */
    'conversions',
    /** Server-side conversions from studio (Meta CAPI, GA4 Measurement Protocol). */
    'serverSide',
    /** Honours Google Consent Mode v2 signals. */
    'consentModeV2',
    'heatmaps',
    'sessionRecording',
    /** Loads other tags itself (Google Tag Manager). */
    'tagContainer',
];
exports.MARKETING_PLUGIN = {
    id: 'marketing',
    version: '1.0.0',
    kind: 'capability',
    trust: 'first-party',
    title: 'Marketing',
    description: 'Analytics and ad pixels, cookie consent, campaign attribution (revenue per ad campaign) and server-side conversions.',
    icon: 'i-ph:megaphone',
    surfaces: ['web-app'],
    agentCard: 'Marketing: GA4/Google Ads/Meta/TikTok/LinkedIn/Clarity/GTM tags, cookie consent, UTM attribution, conversions — configured in Dunea, injected at publish (skills/marketing.md).',
    declares: {
        'marketing.tag': {
            major: 1,
            cardinality: 'many',
            description: 'An analytics or advertising tag loaded on the published site after consent.',
            features: exports.MARKETING_TAG_FEATURES,
        },
    },
    provides: {
        'core.deploy.transform': { major: 1, meta: { order: 10 } },
    },
    subscribes: ['payments.payment.succeeded', 'funnels.lead.captured', 'forms.submitted'],
    emits: {
        'marketing.conversion.sent': { major: 1, description: 'A server-side conversion was delivered to an ad platform.' },
    },
    permissions: ['db:own-tables', 'network:graph.facebook.com', 'network:www.google-analytics.com'],
    targets: {
        'web-app': {
            skills: ['marketing'],
            detect: ['data-dunea-track'],
        },
    },
};
/**
 * Tag providers. `fields[].pattern` is validated in studio before saving, so a
 * typo can't inject arbitrary script into a customer's site.
 */
exports.MARKETING_TAGS = [
    {
        id: 'ga4',
        title: 'Google Analytics 4',
        description: 'Traffic, sources and conversions. Optional server-side purchases via the Measurement Protocol.',
        icon: 'i-ph:chart-line-up',
        consent: 'analytics',
        features: { pageViews: true, conversions: true, serverSide: true, consentModeV2: true },
        fields: [
            {
                key: 'measurementId',
                label: 'Measurement ID',
                placeholder: 'G-XXXXXXXXXX',
                pattern: '^G-[A-Z0-9]{4,16}$',
                required: true,
            },
            {
                key: 'apiSecret',
                label: 'Measurement Protocol API secret (optional)',
                placeholder: 'server-side purchases',
                pattern: '^[A-Za-z0-9_-]{8,64}$',
                secret: true,
            },
        ],
    },
    {
        id: 'google-ads',
        title: 'Google Ads',
        description: 'Conversion tracking and remarketing for Google Ads campaigns.',
        icon: 'i-ph:google-logo',
        consent: 'marketing',
        features: { conversions: true, consentModeV2: true },
        fields: [
            {
                key: 'conversionId',
                label: 'Conversion ID',
                placeholder: 'AW-123456789',
                pattern: '^AW-[0-9]{6,12}$',
                required: true,
            },
            {
                key: 'purchaseLabel',
                label: 'Purchase conversion label',
                placeholder: 'AbC-D_efG-h12_34',
                pattern: '^[A-Za-z0-9_-]{4,40}$',
            },
            { key: 'leadLabel', label: 'Lead conversion label', placeholder: 'XyZ-123', pattern: '^[A-Za-z0-9_-]{4,40}$' },
        ],
    },
    {
        id: 'meta-pixel',
        title: 'Meta Pixel + Conversions API',
        description: 'Facebook & Instagram ads. Add an access token to also send purchases server-side (deduplicated).',
        icon: 'i-ph:meta-logo',
        consent: 'marketing',
        features: { pageViews: true, conversions: true, serverSide: true },
        fields: [
            { key: 'pixelId', label: 'Pixel ID', placeholder: '123456789012345', pattern: '^[0-9]{10,20}$', required: true },
            {
                key: 'accessToken',
                label: 'Conversions API access token (optional)',
                placeholder: 'EAAB…',
                pattern: '^[A-Za-z0-9]{20,400}$',
                secret: true,
            },
        ],
    },
    {
        id: 'tiktok-pixel',
        title: 'TikTok Pixel',
        description: 'Track TikTok ad conversions.',
        icon: 'i-ph:tiktok-logo',
        consent: 'marketing',
        features: { pageViews: true, conversions: true },
        fields: [
            {
                key: 'pixelId',
                label: 'Pixel ID',
                placeholder: 'C4ABCDEF123456789',
                pattern: '^[A-Z0-9]{10,30}$',
                required: true,
            },
        ],
    },
    {
        id: 'linkedin-insight',
        title: 'LinkedIn Insight Tag',
        description: 'B2B ad conversions and audiences on LinkedIn.',
        icon: 'i-ph:linkedin-logo',
        consent: 'marketing',
        features: { pageViews: true, conversions: true },
        fields: [
            { key: 'partnerId', label: 'Partner ID', placeholder: '1234567', pattern: '^[0-9]{4,12}$', required: true },
        ],
    },
    {
        id: 'microsoft-clarity',
        title: 'Microsoft Clarity',
        description: 'Free heatmaps and session recordings.',
        icon: 'i-ph:cursor-click',
        consent: 'analytics',
        features: { heatmaps: true, sessionRecording: true },
        fields: [
            { key: 'projectId', label: 'Project ID', placeholder: 'abcd1234ef', pattern: '^[a-z0-9]{6,20}$', required: true },
        ],
    },
    {
        id: 'google-tag-manager',
        title: 'Google Tag Manager',
        description: 'Manage any other tags yourself. Loaded after marketing consent.',
        icon: 'i-ph:tag',
        consent: 'marketing',
        features: { tagContainer: true, consentModeV2: true },
        fields: [
            {
                key: 'containerId',
                label: 'Container ID',
                placeholder: 'GTM-XXXXXXX',
                pattern: '^GTM-[A-Z0-9]{4,12}$',
                required: true,
            },
        ],
    },
];
exports.MARKETING_TAG_PLUGINS = exports.MARKETING_TAGS.map((t) => ({
    id: t.id,
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: t.title,
    description: t.description,
    icon: t.icon,
    provides: { 'marketing.tag': { major: 1, features: t.features, meta: { consent: t.consent } } },
    settings: [
        {
            id: 'tag',
            title: t.title,
            fields: t.fields.map((f) => ({
                key: f.key,
                label: f.label,
                placeholder: f.placeholder,
                type: f.secret ? 'secret' : 'text',
                required: f.required,
            })),
        },
    ],
}));
exports.SEARCH_CONSOLE_PLUGIN = {
    id: 'search-console',
    version: '1.0.0',
    kind: 'capability',
    trust: 'first-party',
    title: 'Google Search Console',
    description: 'Connect Google, verify your site automatically, submit the sitemap, and see the searches people find you with.',
    icon: 'i-ph:magnifying-glass',
    surfaces: ['web-app'],
    agentCard: 'Search Console: Google verification, sitemap submission and search performance — connected in the Marketing panel.',
    provides: {
        // Adds the google-site-verification meta tag to the published <head>.
        'core.deploy.transform': { major: 1, meta: { order: 5 } },
    },
    emits: {
        'search-console.verified': { major: 1, description: 'Google verified ownership of the site.' },
    },
    permissions: ['oauth:google', 'network:www.googleapis.com'],
};
exports.WHATSAPP_CHAT_PLUGIN = {
    id: 'whatsapp-chat',
    version: '1.0.0',
    kind: 'capability',
    trust: 'first-party',
    title: 'WhatsApp chat button',
    description: 'A floating "Chat on WhatsApp" button with a pre-filled message.',
    icon: 'i-ph:whatsapp-logo',
    surfaces: ['web-app'],
    agentCard: 'WhatsApp chat: floating click-to-chat button, configured in the Marketing panel (no code).',
    provides: {
        'core.deploy.transform': { major: 1, meta: { order: 20 } },
    },
};
